import { useState, useEffect, useRef } from 'react';
import { UserPlus, Sparkles, Calculator, Compass } from 'lucide-react';
import { useLocation } from 'react-router-dom';

/**
 * FloatingTabGroup - A group of floating tabs on the right edge of the screen
 * All tabs move together as a single unit when dragged
 */
export default function FloatingTabGroup({ 
  onCalcClick,
  onCompassClick,
  onNewLeadClick,
  onQuickStartClick,
  calcOpen,
  compassOpen,
  darkMode = false
}) {
  const location = useLocation();

  // Group position (percentage from top)
  const [groupY, setGroupY] = useState(() => {
    const saved = localStorage.getItem('floating_tabs_y');
    return saved ? parseFloat(saved) : 20; // Default to upper third
  });
  
  // SH-2 — below 900px four tabs down the right edge eat the page, so they
  // fold into one "Tools" tab that opens them as a stack.
  const [compact, setCompact] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 899px)').matches);
  const [stackOpen, setStackOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 899px)');
    const read = (e) => { setCompact(e.matches); if (!e.matches) setStackOpen(false); };
    mq.addEventListener('change', read);
    return () => mq.removeEventListener('change', read);
  }, []);

  useEffect(() => {
    if (!stackOpen) return undefined;
    const away = (e) => { if (!groupRef.current?.contains(e.target)) setStackOpen(false); };
    window.addEventListener('pointerdown', away);
    return () => window.removeEventListener('pointerdown', away);
  }, [stackOpen]);

  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef(null);
  const wasDraggingRef = useRef(false);
  const groupRef = useRef(null);
  
  // Save position to localStorage
  useEffect(() => {
    localStorage.setItem('floating_tabs_y', groupY.toString());
  }, [groupY]);
  
  // Drag handlers
  const onDragStart = (e) => {
    e.preventDefault();
    dragStartRef.current = {
      y: e.clientY,
      startY: groupY,
      time: Date.now()
    };
    
    const onMouseMove = (moveEvent) => {
      if (!dragStartRef.current) return;
      const deltaY = moveEvent.clientY - dragStartRef.current.y;
      const distance = Math.abs(deltaY);
      
      // Only start dragging after moving more than 5px
      if (distance > 5 && !isDragging) {
        setIsDragging(true);
        wasDraggingRef.current = true;
      }
      
      if (isDragging || distance > 5) {
        const windowHeight = window.innerHeight;
        const deltaPercent = (deltaY / windowHeight) * 100;
        const newY = Math.max(5, Math.min(70, dragStartRef.current.startY + deltaPercent));
        setGroupY(newY);
      }
    };
    
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      
      dragStartRef.current = null;
      setIsDragging(false);
      
      // Reset wasDragging after a brief delay
      setTimeout(() => {
        wasDraggingRef.current = false;
      }, 100);
    };
    
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };
  
  // Tab configuration
  const tabs = [
    {
      id: 'newlead',
      label: 'LEAD',
      icon: '👤',
      color: '#10B981', // emerald
      hoverBg: 'hover:bg-emerald-50',
      activeBg: 'bg-emerald-500',
      borderColor: 'border-emerald-500/30',
      textColor: 'text-emerald-600',
      onClick: onNewLeadClick,
      testId: 'newlead-tab',
      title: 'Add New Lead'
    },
    {
      id: 'quickstart',
      label: 'START',
      icon: '✨',
      color: '#8B5CF6', // violet
      hoverBg: 'hover:bg-violet-50',
      activeBg: 'bg-violet-500',
      borderColor: 'border-violet-500/30',
      textColor: 'text-violet-600',
      onClick: onQuickStartClick,
      testId: 'quickstart-tab',
      title: 'Quick Start New Project'
    },
    {
      id: 'calc',
      label: 'CALC',
      icon: '📐',
      color: '#BC5D41',
      hoverBg: 'hover:bg-[#BC5D41]/10',
      activeBg: 'bg-[#BC5D41]',
      borderColor: 'border-[#BC5D41]/30',
      textColor: 'text-[#BC5D41]',
      onClick: onCalcClick,
      isOpen: calcOpen,
      testId: 'calculator-tab',
      title: 'Open Calculators (Ctrl+K)'
    },
    {
      id: 'compass',
      label: 'COMPASS',
      icon: null,
      IconComponent: Compass,
      color: '#70170F',
      hoverBg: 'hover:bg-[#70170F]/10',
      activeBg: 'bg-[#70170F]',
      borderColor: 'border-[#70170F]/30',
      textColor: 'text-[#70170F]',
      onClick: onCompassClick,
      isOpen: compassOpen,
      testId: 'compass-tab',
      title: 'Open Compass Guide'
    }
  ];
  
  const handleTabClick = (tab) => {
    // Don't trigger click if we just finished dragging
    if (wasDraggingRef.current) return;
    setStackOpen(false);
    tab.onClick?.();
  };
  // Hide during consultation presentations
  if (location.pathname.startsWith('/consultation/')) return null;

  if (compact) {
    return (
      <div ref={groupRef} className="km-tabs km-tabs--compact" style={{ top: `${groupY}%` }}
        data-testid="floating-tab-group">
        <button type="button" className="km-tabs__one" onClick={() => setStackOpen(o => !o)}
          aria-expanded={stackOpen} data-testid="floating-tabs-one">Tools</button>
        {stackOpen && (
          <div className="km-tabs__stack" data-testid="floating-tabs-stack">
            {tabs.map((tab) => {
              const IconComponent = tab.IconComponent;
              return (
                <button type="button" key={tab.id} onClick={() => handleTabClick(tab)}
                  className={`flex items-center justify-center gap-2 px-2 ${tab.textColor}`}
                  title={tab.title} data-testid={tab.testId}>
                  {IconComponent ? <IconComponent className="w-4 h-4" /> : <span className="text-sm">{tab.icon}</span>}
                  <span className="font-medium whitespace-nowrap" style={{ fontSize: '9px', letterSpacing: '0.5px' }}>
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={groupRef}
      onMouseDown={onDragStart}
      className={`fixed right-0 z-[var(--km-z-rail)] flex flex-col gap-1 transition-all duration-200 select-none ${
        isDragging ? 'cursor-grabbing scale-105' : 'cursor-grab'
      }`}
      style={{ 
        top: `${groupY}%`,
        transform: 'translateY(0)'
      }}
      data-testid="floating-tab-group"
    >
      {tabs.map((tab, index) => {
        const isActive = tab.isOpen || isDragging;
        const IconComponent = tab.IconComponent;
        
        return (
          <div
            key={tab.id}
            onClick={() => handleTabClick(tab)}
            className={`flex flex-col items-center justify-center gap-1 px-1 py-2 border border-r-0 rounded-l-md shadow-md transition-all duration-200 ${
              isDragging
                ? `${tab.activeBg} border-transparent`
                : isActive
                  ? `${tab.activeBg} border-transparent`
                  : darkMode
                    ? `bg-[#1A1A1A] ${tab.borderColor} ${tab.textColor} hover:bg-[#2A2A2A]`
                    : `bg-[#FDFCFB]/95 ${tab.borderColor} ${tab.textColor} ${tab.hoverBg} hover:border-opacity-50`
            }`}
            style={{ width: '28px' }}
            title={tab.title}
            data-testid={tab.testId}
          >
            {/* Icon */}
            {IconComponent ? (
              <IconComponent className={`w-4 h-4 ${isActive || isDragging ? 'text-white' : ''}`} />
            ) : (
              <span className={`text-sm ${isDragging ? 'text-white' : ''}`}>{tab.icon}</span>
            )}
            
            {/* Label */}
            <span 
              className={`font-medium whitespace-nowrap ${isActive || isDragging ? 'text-white' : ''}`}
              style={{ 
                writingMode: 'vertical-lr', 
                textOrientation: 'mixed',
                fontSize: '9px',
                letterSpacing: '0.5px'
              }}
            >
              {tab.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}
